import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './CreateNewItemModal.css';
import {
  FaTimes,
  FaExclamationTriangle
} from 'react-icons/fa';

const FORBIDDEN_CHAR_REGEX = /[,\\'"]/;
const COMPOSITION_RESTRICT_REGEX = /[^a-zA-Z0-9\s%;.]/;

const INITIAL_ITEM_FORM = {
  // Section 1: Item Details

  extension: '',
  itemType: '',
  packType: '',
  category: '',
  scheduleType: '',
  composition: '',
  manufacturer: '',
  brandName: '',
  hsn: '',
  gstCategory: '12',
  rack: '',
  thresholdQuantity: '',
  fastThresholdValue: '',
  slowThresholdValue: '',

  // Section 2: Current Batch Details
  isPacket: 'no', // 'yes' | 'no'
  packQuantity: '10', // units per pack/strip
  quantity: '',
  freeQuantity: '',
  batchNumber: '',
  hasExpiryDate: 'yes', // 'yes' | 'no'
  expiryDate: '',
  packRate: '',
  packMrp: '',
  packMrpDiscount: '0',
  packMrpDiscountType: '%',
  discountPercentage: '0',
  supplierDiscountType: '%'
};

const CreateNewItemModal = ({
  isOpen,
  onClose,
  onSave,
  initialSearchName = ''
}) => {
  const [formData, setFormData] = useState(INITIAL_ITEM_FORM);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...INITIAL_ITEM_FORM,
        brandName: initialSearchName || ''
      });
      setErrorMessage('');
      setFieldErrors({});
    }
  }, [isOpen, initialSearchName]);

  // Selling MRP Calculation (Percent or Rs based on packMrpDiscountType)
  const sellingMrp = useMemo(() => {
    const mrp = Number(formData.packMrp) || 0;
    const discVal = Number(formData.packMrpDiscount) || 0;
    const isPercent = (formData.packMrpDiscountType || '%') === '%';
    const discountAmount = isPercent ? (mrp * discVal) / 100 : discVal;
    const calc = mrp - discountAmount;
    return calc >= 0 ? calc.toFixed(2) : '0.00';
  }, [formData.packMrp, formData.packMrpDiscount, formData.packMrpDiscountType]);

  if (!isOpen) return null;

  // Validation on input changes
  const handleInputChange = (field, value) => {
    // Special character validations according to UI guidelines
    if (['brandName', 'name', 'composition', 'manufacturer', 'hsn'].includes(field)) {
      if (COMPOSITION_RESTRICT_REGEX.test(value)) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: 'Note: special characters space, %, ; and . are only allowed'
        }));
      } else {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    } else if (['rack', 'batchNumber'].includes(field)) {
      if (FORBIDDEN_CHAR_REGEX.test(value)) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: 'Note: special characters comma, double quotes and single quotes are not allowed'
        }));
      } else {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Required fields check
    if (!formData.brandName || !formData.brandName.trim()) {
      setErrorMessage('Brand Name is required.');
      return;
    }

    if (!formData.itemType) {
      setErrorMessage('Item Type is required.');
      return;
    }
    if (!formData.packType) {
      setErrorMessage('Pack Type is required.');
      return;
    }
    if (!formData.category) {
      setErrorMessage('Category is required.');
      return;
    }
    if (!formData.gstCategory) {
      setErrorMessage('GST Category is required.');
      return;
    }
    if (!formData.quantity || Number(formData.quantity) <= 0) {
      setErrorMessage('Quantity is required and must be greater than 0.');
      return;
    }
    if (!formData.batchNumber.trim()) {
      setErrorMessage('Batch Number is required.');
      return;
    }
    if (!formData.packRate || Number(formData.packRate) <= 0) {
      setErrorMessage('Pack Rate is required and must be greater than 0.');
      return;
    }
    if (!formData.packMrp || Number(formData.packMrp) <= 0) {
      setErrorMessage('Pack MRP is required and must be greater than 0.');
      return;
    }
    if (Object.keys(fieldErrors).length > 0) {
      setErrorMessage('Please fix all field warnings before submitting.');
      return;
    }

    setIsSubmitting(true);
    try {
      const rateVal = Number(formData.packRate) || 0;
      const mrpVal = Number(formData.packMrp) || 0;
      const gstVal = Number(formData.gstCategory) || 12;
      const discVal = Number(formData.discountPercentage) || 0;
      const qtyVal = Number(formData.quantity) || 1;

      const backendPayload = {

        extension: formData.extension,
        itemType: formData.itemType,
        packType: formData.packType,
        category: formData.category,
        scheduleType: formData.scheduleType,
        composition: formData.composition,
        medicineBrand: formData.brandName || '',
        hsnCode: formData.hsn,
        medicinePrice: mrpVal,
        packMrp: mrpVal,
        packRate: rateVal,
        gstPercent: gstVal,
        rack: formData.rack,
        thresholdQuantity: formData.thresholdQuantity ? Number(formData.thresholdQuantity) : null,
        fastThresholdValue: formData.fastThresholdValue ? Number(formData.fastThresholdValue) : null,
        slowThresholdValue: formData.slowThresholdValue ? Number(formData.slowThresholdValue) : null,
        stockQuantity: 0,
        unit: formData.packType || 'strip',
        expiryDate: formData.hasExpiryDate === 'yes' && formData.expiryDate ? formData.expiryDate : null,
        isAvailable: false,
        batchNumber: formData.batchNumber,
        packQuantity: formData.isPacket === 'yes' ? (Number(formData.packQuantity) || 10) : 1
      };

      let savedMedicine = null;
      try {
        const res = await axios.post(ENDPOINTS.CREATE_MEDICINE_ENTRY, backendPayload);
        savedMedicine = res.data;
      } catch (apiErr) {
        console.warn('Backend API save warning, proceeding with local fallback:', apiErr);
      }

      const createdItem = {
        id: savedMedicine?.medicineId || Date.now(),
        medicineId: savedMedicine?.medicineId || null,
        itemName: formData.brandName || '',
        rawName: formData.brandName || '',
        extension: formData.extension,
        itemType: formData.itemType,
        packType: formData.packType,
        category: formData.category,
        scheduleType: formData.scheduleType,
        composition: formData.composition,
        manufacturer: formData.manufacturer,
        brand: formData.brandName || '',
        hsn: formData.hsn,
        hsnCode: formData.hsn,
        gstPercent: gstVal,
        rack: formData.rack,
        batchNo: formData.batchNumber,
        batchNumber: formData.batchNumber,
        expiryDate: formData.hasExpiryDate === 'yes' ? formData.expiryDate : '',
        packQuantity: formData.isPacket === 'yes' ? (Number(formData.packQuantity) || 10) : 1,
        quantity: qtyVal,
        freeQuantity: Number(formData.freeQuantity) || 0,
        rate: rateVal,
        packRate: rateVal,
        ratePerUnit: (() => { const pq = formData.isPacket === 'yes' ? (Number(formData.packQuantity) || 10) : 1; return pq > 0 ? rateVal / pq : rateVal; })(),
        mrp: mrpVal,
        packMrp: mrpVal,
        mrpPerUnit: (() => { const pq = formData.isPacket === 'yes' ? (Number(formData.packQuantity) || 10) : 1; return pq > 0 ? mrpVal / pq : mrpVal; })(),
        isUnitRate: false,
        discountPercent: (formData.supplierDiscountType || '%') === '%' ? discVal : 0,
        discountType: formData.supplierDiscountType || '%',
        discountValue: discVal,
        packMrpDiscount: Number(formData.packMrpDiscount) || 0,
        packMrpDiscountType: formData.packMrpDiscountType || '%',
        sellingMrp: Number(sellingMrp),
        stockQuantity: 0,
        availableUnits: 0,
        availableQuantity: 0,
        availablePacks: 0,
        stockBalance: 0
      };

      if (onSave) {
        onSave(createdItem);
      }
      onClose();
    } catch (err) {
      setErrorMessage(err.message || 'Failed to save item.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="vt-item-modal-backdrop" onClick={onClose}>
      <div className="vt-item-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="vt-item-modal-header">
          <h3 className="vt-item-modal-title">Create new Item</h3>
          <button
            type="button"
            className="vt-item-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <form onSubmit={handleSubmit} className="vt-item-modal-form">
          {errorMessage && (
            <div className="vt-item-modal-alert danger">
              <FaExclamationTriangle />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="vt-item-modal-body">
            {/* ─── SECTION 1: Item Details ───────────────────────────────── */}
            <div className="vt-item-section-box">
              <h4 className="vt-item-section-title">Item Details</h4>

              <div className="vt-item-form-group">
                <span className="vt-item-group-label">Item :</span>
                <div className="vt-item-three-col-row">

                  {/* Brand Name */}
                  <div className="vt-item-field-col">
                    <label className="vt-item-label">
                      <span className="req">*</span> Brand Name
                    </label>
                    <input
                      type="text"
                      className={`vt-item-input ${fieldErrors.brandName ? 'has-warning' : ''}`}
                      placeholder="Brand Name (e.g. DOLO)"
                      value={formData.brandName}
                      onChange={(e) => handleInputChange('brandName', e.target.value)}
                      required
                      autoFocus
                    />
                    <span className="vt-item-field-hint">
                      {fieldErrors.brandName || 'Note: special characters space, %, ; and . are only allowed'}
                    </span>
                  </div>

                  {/* Extension */}
                  <div className="vt-item-field-col">
                    <label className="vt-item-label">Extension</label>
                    <input
                      type="text"
                      className="vt-item-input"
                      placeholder="Dosage, size, length"
                      value={formData.extension}
                      onChange={(e) => handleInputChange('extension', e.target.value)}
                    />
                  </div>

                  {/* Item Type */}
                  <div className="vt-item-field-col">
                    <label className="vt-item-label">
                      <span className="req">*</span> Item type
                    </label>
                    <select
                      className="vt-item-select"
                      value={formData.itemType}
                      onChange={(e) => handleInputChange('itemType', e.target.value)}
                      required
                    >
                      <option value="">Select type</option>
                      <option value="TABLET">Tablet</option>
                      <option value="CAPSULE">Capsule</option>
                      <option value="SYRUP">Syrup</option>
                      <option value="INJECTION">Injection</option>
                      <option value="OINTMENT">Ointment</option>
                      <option value="DROPS">Drops</option>
                      <option value="GEL">Gel</option>
                      <option value="SUSPENSION">Suspension</option>
                      <option value="POWDER">Powder</option>
                      <option value="DEVICE">Medical Device</option>
                      <option value="CONSUMABLE">Consumable</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Pack Type */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  <span className="req">*</span> Pack Type
                </label>
                <div className="vt-item-input-wrapper">
                  <select
                    className="vt-item-select full"
                    value={formData.packType}
                    onChange={(e) => handleInputChange('packType', e.target.value)}
                    required
                  >
                    <option value="">Select Pack Type</option>
                    <option value="STRIP">Strip</option>
                    <option value="BOTTLE">Bottle</option>
                    <option value="BOX">Box</option>
                    <option value="VIAL">Vial</option>
                    <option value="AMPOULE">Ampoule</option>
                    <option value="TUBE">Tube</option>
                    <option value="PACKET">Packet</option>
                    <option value="SACHET">Sachet</option>
                    <option value="BLISTER">Blister</option>
                  </select>
                </div>
              </div>

              {/* Category */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  <span className="req">*</span> Category
                </label>
                <div className="vt-item-input-wrapper">
                  <select
                    className="vt-item-select full"
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="ALLOPATHIC">Allopathic</option>
                    <option value="GENERIC">Generic</option>
                    <option value="AYURVEDIC">Ayurvedic</option>
                    <option value="SURGICAL">Surgical</option>
                    <option value="OTC">OTC (Over The Counter)</option>
                    <option value="ANTIBIOTIC">Antibiotic</option>
                    <option value="ANALGESIC">Analgesic / Pain Killer</option>
                    <option value="ANTACID">Antacid</option>
                  </select>
                </div>
              </div>

              {/* Schedule Type */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">Schedule Type</label>
                <div className="vt-item-input-wrapper">
                  <select
                    className="vt-item-select full"
                    value={formData.scheduleType}
                    onChange={(e) => handleInputChange('scheduleType', e.target.value)}
                  >
                    <option value="">Select Schedule Type</option>
                    <option value="SCHEDULE_H">Schedule H</option>
                    <option value="SCHEDULE_H1">Schedule H1</option>
                    <option value="SCHEDULE_X">Schedule X</option>
                    <option value="SCHEDULE_G">Schedule G</option>
                    <option value="NON_SCHEDULED">Non-Scheduled</option>
                    <option value="OTC">OTC</option>
                  </select>
                </div>
              </div>

              {/* Composition */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">Composition</label>
                <div className="vt-item-input-wrapper">
                  <textarea
                    rows={2}
                    className={`vt-item-textarea ${fieldErrors.composition ? 'has-warning' : ''}`}
                    placeholder="e.g. Paracetamol IP 650mg"
                    value={formData.composition}
                    onChange={(e) => handleInputChange('composition', e.target.value)}
                  />
                  <span className="vt-item-field-hint">
                    {fieldErrors.composition || 'Note: special characters space, %, ; and . are only allowed'}
                  </span>
                </div>
              </div>



              {/* Manufacturer */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">Manufacturer</label>
                <div className="vt-item-input-wrapper">
                  <input
                    type="text"
                    className={`vt-item-input full ${fieldErrors.manufacturer ? 'has-warning' : ''}`}
                    placeholder="Manufacturer Name"
                    value={formData.manufacturer}
                    onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                  />
                  <span className="vt-item-field-hint">
                    {fieldErrors.manufacturer || 'Note: special characters space, %, ; and . are only allowed'}
                  </span>
                </div>
              </div>

              {/* HSN */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">HSN</label>
                <div className="vt-item-input-wrapper">
                  <input
                    type="text"
                    className={`vt-item-input full ${fieldErrors.hsn ? 'has-warning' : ''}`}
                    placeholder="e.g. 300490"
                    value={formData.hsn}
                    onChange={(e) => handleInputChange('hsn', e.target.value)}
                  />
                  <span className="vt-item-field-hint">
                    {fieldErrors.hsn || 'Note: special characters space, %, ; and . are only allowed'}
                  </span>
                </div>
              </div>

              {/* GST Category with Edit Button Beside */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  <span className="req">*</span> GST Category
                </label>
                <div className="vt-item-input-wrapper flex-row">
                  <select
                    className="vt-item-select full"
                    value={formData.gstCategory}
                    onChange={(e) => handleInputChange('gstCategory', e.target.value)}
                    required
                  >
                    <option value="">Select gst category</option>
                    <option value="0">0% (Exempted)</option>
                    <option value="5">5% GST</option>
                    <option value="12">12% GST</option>
                    <option value="18">18% GST</option>
                    <option value="28">28% GST</option>
                  </select>
                  <button type="button" className="vt-item-edit-tag-btn" onClick={() => {}}>
                    Edit
                  </button>
                </div>
              </div>

              {/* Rack */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">Rack</label>
                <div className="vt-item-input-wrapper">
                  <input
                    type="text"
                    className={`vt-item-input full ${fieldErrors.rack ? 'has-warning' : ''}`}
                    placeholder="Rack / Shelf No"
                    value={formData.rack}
                    onChange={(e) => handleInputChange('rack', e.target.value)}
                  />
                  <span className="vt-item-field-hint">
                    {fieldErrors.rack || 'Note: special characters comma, double quotes and single quotes are not allowed'}
                  </span>
                </div>
              </div>

              {/* Threshold quantity */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">Threshold quantity</label>
                <div className="vt-item-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    className="vt-item-input full"
                    placeholder="0"
                    value={formData.thresholdQuantity}
                    onChange={(e) => handleInputChange('thresholdQuantity', e.target.value)}
                  />
                </div>
              </div>

              {/* Fast Threshold value */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">Fast Threshold value</label>
                <div className="vt-item-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    className="vt-item-input full"
                    placeholder="0"
                    value={formData.fastThresholdValue}
                    onChange={(e) => handleInputChange('fastThresholdValue', e.target.value)}
                  />
                </div>
              </div>

              {/* Slow Threshold Value */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">Slow Threshold Value</label>
                <div className="vt-item-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    className="vt-item-input full"
                    placeholder="0"
                    value={formData.slowThresholdValue}
                    onChange={(e) => handleInputChange('slowThresholdValue', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* ─── SECTION 2: Current batch details ──────────────────────── */}
            <div className="vt-item-section-box">
              <h4 className="vt-item-section-title">Current batch details</h4>

              {/* Is this item a packet? */}
              <div className="vt-item-form-row radio-row">
                <label className="vt-item-label">
                  <span className="req">*</span> Is this item a packet?
                </label>
                <div className="vt-item-radio-group">
                  <label className="vt-item-radio-label">
                    <input
                      type="radio"
                      name="isPacket"
                      value="yes"
                      checked={formData.isPacket === 'yes'}
                      onChange={() => handleInputChange('isPacket', 'yes')}
                    />
                    Yes
                  </label>
                  <label className="vt-item-radio-label">
                    <input
                      type="radio"
                      name="isPacket"
                      value="no"
                      checked={formData.isPacket === 'no'}
                      onChange={() => handleInputChange('isPacket', 'no')}
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Pack Quantity (only when isPacket = yes) */}
              {formData.isPacket === 'yes' && (
                <div className="vt-item-form-row">
                  <label className="vt-item-label">
                    <span className="req">*</span> Pack Quantity per {formData.packType ? formData.packType.toLowerCase() : 'pack'}
                  </label>
                  <div className="vt-item-input-wrapper flex-row">
                    <input
                      type="number"
                      min="1"
                      className="vt-item-input"
                      style={{ width: '120px', flexShrink: 0 }}
                      placeholder="10"
                      value={formData.packQuantity}
                      onChange={(e) => handleInputChange('packQuantity', e.target.value)}
                      required
                    />
                    <span className="vt-item-suffix-label">per strip</span>
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  <span className="req">*</span> {formData.isPacket === 'yes' ? 'No of Packets' : 'Quantity'}
                </label>
                <div className="vt-item-input-wrapper flex-row">
                  <input
                    type="number"
                    min="1"
                    className="vt-item-input"
                    style={{ flex: '1 1 auto', minWidth: '80px', maxWidth: '200px' }}
                    placeholder={formData.isPacket === 'yes' ? 'No of strips' : 'Quantity'}
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    required
                  />
                  {formData.isPacket === 'yes' && (
                    <span className="vt-item-suffix-label">strips</span>
                  )}
                </div>
              </div>

              {/* Free quantity */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">{formData.isPacket === 'yes' ? 'Free packets' : 'Free quantity'}</label>
                <div className="vt-item-input-wrapper flex-row">
                  <input
                    type="number"
                    min="0"
                    className="vt-item-input"
                    style={{ flex: '1 1 auto', minWidth: '80px', maxWidth: '200px' }}
                    placeholder="0"
                    value={formData.freeQuantity}
                    onChange={(e) => handleInputChange('freeQuantity', e.target.value)}
                  />
                  {formData.isPacket === 'yes' && (
                    <span className="vt-item-suffix-label">strips</span>
                  )}
                </div>
              </div>

              {/* Batch number */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  <span className="req">*</span> Batch number
                </label>
                <div className="vt-item-input-wrapper">
                  <input
                    type="text"
                    className={`vt-item-input full ${!formData.batchNumber ? 'batch-required-highlight' : ''}`}
                    placeholder="Batch Number"
                    value={formData.batchNumber}
                    onChange={(e) => handleInputChange('batchNumber', e.target.value)}
                    required
                  />
                  <span className="vt-item-field-hint">
                    {fieldErrors.batchNumber || 'Note: special characters comma, double quotes and single quotes are not allowed'}
                  </span>
                </div>
              </div>

              {/* Does this item have an expiry date ? */}
              <div className="vt-item-form-row radio-row">
                <label className="vt-item-label">
                  <span className="req">*</span> Does this item have an expiry date ?
                </label>
                <div className="vt-item-radio-group">
                  <label className="vt-item-radio-label">
                    <input
                      type="radio"
                      name="hasExpiryDate"
                      value="yes"
                      checked={formData.hasExpiryDate === 'yes'}
                      onChange={() => handleInputChange('hasExpiryDate', 'yes')}
                    />
                    Yes
                  </label>
                  <label className="vt-item-radio-label">
                    <input
                      type="radio"
                      name="hasExpiryDate"
                      value="no"
                      checked={formData.hasExpiryDate === 'no'}
                      onChange={() => handleInputChange('hasExpiryDate', 'no')}
                    />
                    No
                  </label>
                </div>
              </div>

              {formData.hasExpiryDate === 'yes' && (
                <div className="vt-item-form-row">
                  <label className="vt-item-label">Expiry Date</label>
                  <div className="vt-item-input-wrapper">
                    <input
                      type="date"
                      className="vt-item-input full"
                      value={formData.expiryDate}
                      onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Pack Rate */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  <span className="req">*</span> Pack Rate
                </label>
                <div className="vt-item-input-wrapper">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="vt-item-input full"
                    placeholder="0.00"
                    value={formData.packRate}
                    onChange={(e) => handleInputChange('packRate', e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Pack Mrp + Selling MRP Green Note Beside */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  <span className="req">*</span> Pack Mrp
                </label>
                <div className="vt-item-mrp-wrapper">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="vt-item-input"
                    style={{ width: '140px', flexShrink: 0 }}
                    placeholder="0"
                    value={formData.packMrp}
                    onChange={(e) => handleInputChange('packMrp', e.target.value)}
                    required
                  />
                  <span className="vt-item-selling-mrp-note">
                    Selling MRP = ₹ {sellingMrp} | MRP / Unit: ₹ {(() => {
                      const pq = formData.isPacket === 'yes'
                        ? Math.max(1, Number(formData.packQuantity) || 1)
                        : 1;
                      return (Number(sellingMrp) / pq).toFixed(2);
                    })()}
                  </span>
                </div>
              </div>

              {/* Discount on MRP */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  Discount on MRP
                </label>
                <div className="vt-item-discount-group">
                  <input
                    type="number"
                    step={formData.packMrpDiscountType === '₹' ? '0.01' : '0.1'}
                    min="0"
                    max={formData.packMrpDiscountType === '%' ? '100' : undefined}
                    className="vt-item-input full"
                    placeholder="0"
                    value={formData.packMrpDiscount}
                    onChange={(e) => handleInputChange('packMrpDiscount', e.target.value)}
                  />
                  <div className="vt-item-unit-toggle">
                    <button
                      type="button"
                      className={`vt-item-unit-btn ${formData.packMrpDiscountType === '%' ? 'active' : ''}`}
                      onClick={() => handleInputChange('packMrpDiscountType', '%')}
                    >
                      %
                    </button>
                    <button
                      type="button"
                      className={`vt-item-unit-btn ${formData.packMrpDiscountType === '₹' ? 'active' : ''}`}
                      onClick={() => handleInputChange('packMrpDiscountType', '₹')}
                    >
                      ₹
                    </button>
                  </div>
                </div>
              </div>

              {/* Discount given by supplier */}
              <div className="vt-item-form-row">
                <label className="vt-item-label">
                  Discount given by supplier
                </label>
                <div className="vt-item-discount-group">
                  <input
                    type="number"
                    step={formData.supplierDiscountType === '₹' ? '0.01' : '0.1'}
                    min="0"
                    max={formData.supplierDiscountType === '%' ? '100' : undefined}
                    className="vt-item-input full"
                    placeholder="0"
                    value={formData.discountPercentage}
                    onChange={(e) => handleInputChange('discountPercentage', e.target.value)}
                  />
                  <div className="vt-item-unit-toggle">
                    <button
                      type="button"
                      className={`vt-item-unit-btn ${formData.supplierDiscountType === '%' ? 'active' : ''}`}
                      onClick={() => handleInputChange('supplierDiscountType', '%')}
                    >
                      %
                    </button>
                    <button
                      type="button"
                      className={`vt-item-unit-btn ${formData.supplierDiscountType === '₹' ? 'active' : ''}`}
                      onClick={() => handleInputChange('supplierDiscountType', '₹')}
                    >
                      ₹
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer Buttons */}
          <div className="vt-item-modal-footer">
            <button
              type="submit"
              className="vt-item-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Submit'}
            </button>
            <button
              type="button"
              className="vt-item-close-btn"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default CreateNewItemModal;
